import React from 'react';

type Direction = 'horizontal' | 'vertical';

export function useRovingTabIndex(maxItems: number, onEnter: () => void, direction: Direction = 'horizontal') {
    const [selectedIndex, setSelectedIndex] = React.useState<number>(0);
    const panelRef = React.useRef<HTMLDivElement | null>(null);
    const handlerRef = React.useRef<(e: KeyboardEvent) => void>();

    // Clamp selected index when item count shrinks (e.g. filter applied)
    React.useEffect(() => {
        setSelectedIndex((i) => (i >= maxItems ? Math.max(0, maxItems - 1) : i));
    }, [maxItems]);

    handlerRef.current = (e: KeyboardEvent) => {
        if (maxItems <= 0) {
            return;
        }

        const prevKey = direction === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
        const nextKey = direction === 'horizontal' ? 'ArrowRight' : 'ArrowDown';

        if (e.key === prevKey) {
            e.preventDefault();
            setSelectedIndex((i) => (i - 1 < 0 ? maxItems - 1 : i - 1));
        }

        if (e.key === nextKey) {
            e.preventDefault();
            setSelectedIndex((i) => (i + 1 > maxItems - 1 ? 0 : i + 1));
        }

        if (e.key === 'Enter') {
            onEnter();
        }
    };

    React.useEffect(() => {
        if (!panelRef.current || !handlerRef.current) {
            return;
        }

        const panel = panelRef.current;
        const onKeyDown = (e: KeyboardEvent) => {
            handlerRef.current?.(e);
        };

        panel.addEventListener('keydown', onKeyDown);
        return () => panel.removeEventListener('keydown', onKeyDown);
    }, []);

    return { panelRef, selectedIndex, setSelectedIndex };
}
